import type { ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useClientName } from "../hooks/useClientName";
import { useIncomeExpense } from "../hooks/useIncomeExpense";
import type { IncomeExpenseReport } from "../types/report";
import { formatAmount } from "../utils/currency";
import { ErrorMessage } from "../components/ErrorMessage";
import { ClientBreadcrumb, ClientPageHeader } from "../components/ClientPageHeader";
import { PageTransition } from "../components/ui/PageTransition";

interface TotalsProps {
  report: IncomeExpenseReport;
}

/**
 * The three totals under the frontend's own headings, in the currency the
 * report carries (no symbol when it names none), and the dates of the first
 * and last transaction they count, which are not a period. Nothing else: no
 * account line, no trial balance (requirements What 5, revision 2).
 */
function Totals({ report }: TotalsProps): ReactNode {
  const totals = [
    { heading: "Money in", amount: report.total_income },
    { heading: "Money out", amount: report.total_expenses },
    { heading: "What is left", amount: report.net_income },
  ];
  const first = report.first_transaction_date;
  const last = report.last_transaction_date;
  return (
    <>
      {totals.map((total) => (
        <section className="numbers-total" key={total.heading}>
          <h2>{total.heading}</h2>
          <p className="numbers-amount">{formatAmount(total.amount, report.currency)}</p>
        </section>
      ))}
      {first && last && <p className="numbers-dates">{`${first} – ${last}`}</p>}
    </>
  );
}

/**
 * The read-only numbers page of one client's books (`/clients/:clientId/numbers`).
 *
 * When the report carries `errors` (the books could not be read; the server
 * answers 200 with zero totals), each error sentence is shown as the server
 * wrote it and no totals at all, so a zero is never shown as the client's
 * number (gate 2 Risk H2). The page carries the client page's header and the
 * client's name (walk-through round 1, finding 11).
 */
export function NumbersPage(): ReactNode {
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();
  const numericId = clientId ? parseInt(clientId, 10) : NaN;
  const { report, error } = useIncomeExpense(numericId);
  const clientName = useClientName(numericId);
  const clientPath = `/clients/${clientId ?? ""}`;

  return (
    <main className="numbers-page">
      <ClientPageHeader onBack={() => navigate(clientPath)} />
      <PageTransition>
        {clientName && (
          <ClientBreadcrumb clientName={clientName} clientPath={clientPath} current="Numbers" />
        )}
        {error && <ErrorMessage message={error} />}
        {report &&
          report.errors.map((sentence) => <ErrorMessage key={sentence} message={sentence} />)}
        {report && report.errors.length === 0 && <Totals report={report} />}
      </PageTransition>
    </main>
  );
}
